import { describeUA } from '../../lib/ua'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { Button } from '../../components/ui/button'

// DIS-27: Settings › Sessions — list and revoke.
export const Route = createFileRoute('/_app/settings/sessions')({
  component: SessionsPage,
})

const sessionsQuery = queryOptions({
  queryKey: ['sessions'],
  queryFn: async () => {
    const { data, response } = await api.GET('/auth/sessions')
    if (!data) throw new Error(`GET /auth/sessions failed: ${response.status}`)
    return data.sessions
  },
})

function SessionsPage() {
  const { auth } = Route.useRouteContext()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const sessions = useQuery(sessionsQuery)
  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const { response } = await api.DELETE('/auth/sessions/{id}', { params: { path: { id } } })
      if (!response.ok) throw new Error(`revoke failed: ${response.status}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionsQuery.queryKey }),
  })
  // Revoking the current session is a logout: clear local auth state and
  // leave, instead of re-fetching a list we can no longer read.
  async function signOutHere() {
    await auth.logout()
    await navigate({ to: '/login' })
  }

  return (
    <div className="mx-auto w-full max-w-2xl p-6">
      <h1 className="text-lg font-semibold">Sessions</h1>
      <p className="mb-4 text-sm text-fg-muted">Devices and browsers signed in to your account. Revoking signs that device out on its next request.</p>
      {sessions.isPending && <p className="text-sm text-fg-muted">Loading…</p>}
      {sessions.isError && <p className="text-sm text-danger">Couldn't load sessions.</p>}
      <ul className="divide-y divide-border rounded-lg border border-border">
        {sessions.data?.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="min-w-0">
              <div className="truncate text-sm">
                {describeUA(s.user_agent)}
                {s.current && <span className="ml-2 rounded bg-accent/15 px-1.5 py-0.5 text-xs font-medium">This session</span>}
              </div>
              <div className="text-xs text-fg-muted">
                {s.ip ? `${s.ip} · ` : ''}last active {new Date(s.last_seen_at).toLocaleString()}
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => (s.current ? void signOutHere() : revoke.mutate(s.id))}
            >
              {s.current ? 'Sign out' : 'Revoke'}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}
